import React, { useState, useRef } from 'react';
import { trackEvent, trackError } from '../utils/helpers';

// Funktion för att konvertera markdown till HTML
const convertMarkdownToHtml = (text) => {
    if (!text) return text;
    
    let html = text;
    
    // 1. Konvertera fetstil FÖRST: **text** eller __text__
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');
    
    // 2. Konvertera kursiv: *text* eller _text_ (men inte om det redan är del av fetstil)
    html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
    html = html.replace(/(?<!_)_([^_]+)_(?!_)/g, '<em>$1</em>');
    
    // 3. Konvertera länkar: [text](url) - EFTER formattering för att undvika konflikt
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    html = html.replace(markdownLinkRegex, (match, linkText, url) => {
        let domain = '';
        try {
            const urlObj = new URL(url);
            domain = urlObj.hostname.replace('www.', '');
        } catch (e) {
            domain = url;
        }
        
        const isOpenAISource = url.includes('utm_source=openai');
        const title = isOpenAISource ? `Källa: ${domain} (via web search)` : `Källa: ${domain}`;
        
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="source-link" title="${title}">${linkText}</a>`;
    });
    
    // 4. Konvertera punktlistor
    // Först hantera flera rader som börjar med -
    const lines = html.split('\n');
    let inList = false;
    const processedLines = [];
    
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        if (line.startsWith('- ')) {
            if (!inList) {
                processedLines.push('<ul>');
                inList = true;
            }
            processedLines.push(`<li>${line.substring(2).trim()}</li>`);
        } else {
            if (inList) {
                processedLines.push('</ul>');
                inList = false;
            }
            
            if (line) {
                processedLines.push(`<p>${line}</p>`);
            }
        }
    }
    
    // Stäng eventuell öppen lista
    if (inList) {
        processedLines.push('</ul>');
    }
    
    return processedLines.join('');
};

const FollowupQuestions = ({ questions, articleId, onFetchAnswer }) => {
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loadingTexts, setLoadingTexts] = useState({});
  const timersRef = useRef({});

  // Roliga och finurliga laddningsmeddelanden - varje steg har många alternativ
  const loadingMessageStages = [
    // Stage 1: Initial (starting the task)
    [
      "Tänker djupt...",
      "Funderar...",
      "Hmm, intressant...",
      "Startar hjärnkontoret...",
      "Sätter igång...",
      "Rullar upp ärmarna...",
      "Tar sats...",
      "Förbereder...",
      "Laddar upp...",
      "Aktiverar neuroner...",
      "Vaknar till liv...",
      "Sträcker på tankarna...",
      "Värmer upp hjärnan...",
      "Kör igång maskineriet...",
      "Drar igång...",
      "Sätter fart...",
      "Hugger tag i frågan...",
      "Kavlar upp ärmarna...",
      "Tar tag i detta...",
      "Nu kör vi..."
    ],
    // Stage 2: After 1 second (investigating/searching)
    [
      "Nosar runt...",
      "Letar spår...",
      "Sniffar runt...",
      "Kollar läget...",
      "Undersöker...",
      "Snokar...",
      "Spanar...",
      "Kikar i hörnen...",
      "Rotar lite...",
      "Gräver...",
      "Söker...",
      "Petar runt...",
      "Finsöker...",
      "Skummar ytan...",
      "Letar fingervisningar...",
      "Följer spåren...",
      "Jagar ledtrådar...",
      "Plockar fram luppen...",
      "På jakt...",
      "Detektivarbete pågår..."
    ],
    // Stage 3: After 3 seconds (web searching/googling)
    [
      "Googlar som en galning...",
      "Surfar runt...",
      "Webbjägare aktiv...",
      "Söker på internet...",
      "Crawlar webben...",
      "Scrollar som besatt...",
      "Klickar runt...",
      "Internetsurfar...",
      "Nätjägare...",
      "Googlar...",
      "Webbspanar...",
      "Surf-mode: ON...",
      "Nätfiskar...",
      "Webdetektiv igång...",
      "Sökmotorn spanar...",
      "Surfar intensivt...",
      "Cyberspanar...",
      "Digitalspanar...",
      "Klickar loss...",
      "Nätgräver..."
    ],
    // Stage 4: After 5 seconds (analyzing/structuring)
    [
      "Gräver i källorna...",
      "Analyserar...",
      "Knådar informationen...",
      "Sorterar fakta...",
      "Strukturerar...",
      "Pusslar ihop det...",
      "Filtrerar bort skräp...",
      "Väger för och emot...",
      "Kopplar ihop ledtrådar...",
      "Skapar ordning...",
      "Reder ut trådarna...",
      "Bygger upp bilden...",
      "Kombinerar pusselbitar...",
      "Granskar detaljerna...",
      "Väger evidensen...",
      "Kollar källor...",
      "Verifierar...",
      "Dubbelkollar...",
      "Kvalitetsgranskar...",
      "Petar i detaljerna..."
    ],
    // Stage 5: After 7 seconds (finalizing/composing)
    [
      "Knåpar ihop svaret...",
      "Sammanställer...",
      "Finputsar...",
      "Fixar till det...",
      "Skriver klart...",
      "Lägger sista handen...",
      "Plockar fram pärlan...",
      "Formaterar...",
      "Polerar svaret...",
      "Summerar...",
      "Sista finessen...",
      "Rullar ut svaret...",
      "Förpackar snyggt...",
      "Sätter punkt...",
      "Gör färdigt...",
      "Snart klar...",
      "Nästan där...",
      "Finslipar...",
      "Lägger snart av...",
      "Levererar strax..."
    ]
  ];

  // Helper function to get a random message from a stage
  const getRandomMessage = (stageIndex) => {
    const stageMessages = loadingMessageStages[stageIndex];
    const randomIndex = Math.floor(Math.random() * stageMessages.length);
    return stageMessages[randomIndex];
  };

  // Helper function to start progressive loading text
  const startLoadingText = (questionIndex) => {
    // Clear any existing timers for this question
    clearLoadingTimers(questionIndex);
    
    // Set up progressive timers including the initial message
    const timers = [];
    
    // Set initial message with a tiny delay to ensure loading state is set
    const initialTimer = setTimeout(() => {
      setLoadingTexts(prev => ({ ...prev, [questionIndex]: getRandomMessage(0) }));
    }, 50); // Small delay to ensure loading state is rendered first
    
    timers.push(initialTimer);
    
    // Timer delays: 1s, 2s, 2s, 2s (from initial, cumulative: 1s, 3s, 5s, 7s)
    const delays = [1000, 2000, 2000, 2000];
    let cumulativeDelay = 50; // Start from initial delay
    
    for (let i = 1; i < loadingMessageStages.length; i++) {
      cumulativeDelay += delays[i - 1];
      
      const timer = setTimeout(() => {
        setLoadingTexts(prev => ({ ...prev, [questionIndex]: getRandomMessage(i) }));
      }, cumulativeDelay);
      
      timers.push(timer);
    }
    
    // Store timers for cleanup
    timersRef.current[questionIndex] = timers;
  };

  // Helper function to clear loading timers
  const clearLoadingTimers = (questionIndex) => {
    if (timersRef.current[questionIndex]) {
      timersRef.current[questionIndex].forEach(timer => clearTimeout(timer));
      delete timersRef.current[questionIndex];
    }
    // Clear the loading text
    setLoadingTexts(prev => {
      const newTexts = { ...prev };
      delete newTexts[questionIndex];
      return newTexts;
    });
  };

  const handleQuestionClick = async (index) => {
    const isCurrentlyExpanded = expanded[index];
    
    // Track the click
    trackEvent('followup-clicked', {
      articleId,
      questionIndex: index,
      action: isCurrentlyExpanded ? 'collapse' : 'expand',
      hasAnswer: !!answers[index]
    });
    
    // Toggle expanded state
    setExpanded(prev => ({
      ...prev,
      [index]: !prev[index]
    }));

    // If already have answer or currently loading, don't fetch again
    if (answers[index] || loading[index]) {
      return;
    }

    // Fetch answer
    try {
      setLoading(prev => ({ ...prev, [index]: true }));
      
      // Start progressive loading text
      startLoadingText(index);
      
      const response = await onFetchAnswer(articleId, index);
      setAnswers(prev => ({
        ...prev,
        [index]: response.answer || 'Kunde inte hämta svar'
      }));
      
      trackEvent('followup-answer-loaded', {
        articleId,
        questionIndex: index,
        answerLength: response.answer?.length || 0
      });
    } catch (error) {
      console.error('Error fetching followup answer:', error);
      trackError('followup-answer-error', error, {
        articleId,
        questionIndex: index
      });
      setAnswers(prev => ({
        ...prev,
        [index]: 'Ett fel uppstod när svaret skulle hämtas'
      }));
    } finally {
      setLoading(prev => ({ ...prev, [index]: false }));
      clearLoadingTimers(index);
    }
  };

  return (
    <section className="followup-section">
      <h2 className="followup-title">AI Fördjupning</h2>
      <ul className="followup-questions">
        {questions.map((question, index) => (
          <li key={index} className="followup-item">
            <button
              className={`followup-button ${expanded[index] ? 'active' : ''}`}
              onClick={() => handleQuestionClick(index)}
            >
              {question}
            </button>
            {expanded[index] && (
              <div className="followup-answer">
                {loading[index] ? (
                  <div className="loading-spinner">
                    <svg viewBox="0 0 50 50" className="spinner" style={{ width: '30px', height: '30px' }}>
                      <circle cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                    </svg>
                    {loadingTexts[index] && (
                      <div className="followup-loading-text">
                        {loadingTexts[index]}
                      </div>
                    )}
                  </div>
                ) : (
                  answers[index] ? (
                    <div dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(answers[index]) }} />
                  ) : (
                    'Klicka för att ladda svar'
                  )
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default FollowupQuestions;
